import { useState } from 'react';
import { Card, CardHeader, CardTitle, CardBody, Button, Dialog, DialogContent, DialogHeader, DialogTitle, DialogBody, DialogFooter, Chip, Badge } from '@vnetwork/ui/primitives';
import { DataTable, EmptyState, type ColumnDef } from '@vnetwork/ui/data';
import { Field } from '@vnetwork/ui/forms';
import { useToast } from '@vnetwork/ui/feedback';
import { Boxes, Pencil } from 'lucide-react';
import { formatMoney } from '@/lib/format';
import {
  useServiceConfigoptions,
  useServiceAddonConfigoptions,
  type ServiceConfigOption,
  type ServiceDetail,
} from '@/api/services';
import { useUpdateServiceConfigoptions } from '@/api/services-mutations';
import { TabError, TabLoading } from '@/pages/customers/detail/tab-states';
import { NumericInput } from '@/components/numeric-input';

/** Templates whose provisioning config is NOT editable via the generic config tab. */
const NO_CONFIG_EDIT = ['SSL.SSL', 'Domain.VNNIC', 'Domain.TLDs', 'Device.Liquidation'];

const rightHeader = (label: string) => () => <span style={{ display: 'block', textAlign: 'right' }}>{label}</span>;
const num = (v?: number | null) => <span style={{ display: 'block', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{v ?? '—'}</span>;

function errMsg(err: unknown): string {
  const res = (err as { response?: { data?: { error_description?: string; error?: string; message?: string } } })?.response;
  return res?.data?.error_description ?? res?.data?.error ?? res?.data?.message ?? 'Something went wrong.';
}

/**
 * Configuration tab — the service's provisioning/pricing units (VM packages,
 * bandwidth commitments, storage capacity …) from `/configoptions`, plus one
 * editable section per addon service (each saved independently to its own id,
 * mirroring legacy `ServiceEditForm`). Commitment value + unit price are editable
 * while a service is still `waiting-confirm` (per-service backend gate).
 */
export function ServiceConfigTab({ detail }: { detail: ServiceDetail }) {
  const id = detail.service_id;
  const base = useServiceConfigoptions(id);
  const addons = useServiceAddonConfigoptions(id);

  if (base.isError) return <TabError title="Failed to load configuration" onRetry={() => base.refetch()} />;
  if (base.isLoading) return <TabLoading rows={4} />;

  const currency = detail.service_currency || 'VND';
  const mainEditable = detail.service_status === 'waiting-confirm' && !NO_CONFIG_EDIT.includes(detail.service_template);
  const addonList = addons.data?.results ?? [];

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      <ConfigSection serviceId={id} title="Main configuration" rows={base.data?.results ?? []} editable={mainEditable} currency={currency} />
      {addonList.map((a) => (
        <ConfigSection
          key={a.service_id}
          serviceId={a.service_id}
          title={a.service_name}
          isAddon
          rows={a.configoptions ?? []}
          editable={a.service_status === 'waiting-confirm' && !NO_CONFIG_EDIT.includes(a.service_template)}
          currency={currency}
        />
      ))}
    </div>
  );
}

/** One service's configoption table + inline edit — used for the main service and each addon. */
function ConfigSection({
  serviceId,
  title,
  rows,
  editable,
  isAddon,
  currency = 'VND',
}: {
  serviceId: number;
  title: string;
  rows: ServiceConfigOption[];
  editable: boolean;
  isAddon?: boolean;
  currency?: string;
}) {
  const toast = useToast();
  const update = useUpdateServiceConfigoptions(serviceId);
  const [edit, setEdit] = useState<ServiceConfigOption | null>(null);

  const money = (v?: number) => <span style={{ display: 'block', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{formatMoney(v, currency)}</span>;

  const columns: ColumnDef<ServiceConfigOption, unknown>[] = [
    { id: 'name', header: 'Item', cell: ({ row }) => <span style={{ fontWeight: 'var(--vn-font-weight-medium)' }}>{row.original.name}</span> },
    { id: 'type', header: 'Type', cell: ({ row }) => row.original.type || '—' },
    { id: 'unit', header: 'Unit', cell: ({ row }) => row.original.unit || '—' },
    { id: 'commitment_value', header: rightHeader('Commitment'), cell: ({ row }) => num(row.original.commitment_value) },
    { id: 'commitment_price_per_unit', header: rightHeader('Unit price'), cell: ({ row }) => money(row.original.commitment_price_per_unit) },
    { id: 'commitment_price_total', header: rightHeader('Total'), cell: ({ row }) => money(row.original.commitment_price_total) },
    {
      id: 'commitment_prepaid',
      header: 'Prepaid',
      cell: ({ row }) => (
        <Chip appearance="subtle" tone={row.original.commitment_prepaid ? 'success' : 'neutral'} size="sm">
          {row.original.commitment_prepaid ? 'Prepaid' : 'Postpaid'}
        </Chip>
      ),
    },
    { id: 'available', header: rightHeader('Available'), cell: ({ row }) => num(row.original.commitment_available) },
    ...(editable
      ? [
          {
            id: 'actions',
            header: rightHeader('Edit'),
            cell: ({ row }) => (
              <span style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button size="sm" variant="ghost" aria-label="Edit config" onClick={() => setEdit(row.original)}>
                  <Pencil size={16} />
                </Button>
              </span>
            ),
          } as ColumnDef<ServiceConfigOption, unknown>,
        ]
      : []),
  ];

  return (
    <Card flat>
      <CardHeader>
        <CardTitle>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
            {title}
            {isAddon ? <Badge tone="info" appearance="subtle" size="sm">addon</Badge> : null}
          </span>
        </CardTitle>
      </CardHeader>
      <CardBody>
        <DataTable
          columns={columns}
          data={rows}
          getRowId={(r) => String(r.service_configoption_id)}
          emptyState={<EmptyState icon={<Boxes size={24} />} title="No configuration" description="This service has no configuration items." />}
        />
        {edit ? (
          <ConfigEditDialog
            option={edit}
            pending={update.isPending}
            onClose={() => setEdit(null)}
            onSubmit={(commitment_value, commitment_price_per_unit) => {
              update.mutate(
                [{ service_configoption_id: edit.service_configoption_id, commitment_value, commitment_price_per_unit }],
                {
                  onSuccess: () => {
                    toast.success('Configuration updated');
                    setEdit(null);
                  },
                  onError: (e) => toast.error(errMsg(e)),
                },
              );
            }}
          />
        ) : null}
      </CardBody>
    </Card>
  );
}

function ConfigEditDialog({
  option,
  pending,
  onClose,
  onSubmit,
}: {
  option: ServiceConfigOption;
  pending: boolean;
  onClose: () => void;
  onSubmit: (commitmentValue: number, unitPrice: number) => void;
}) {
  const [value, setValue] = useState(String(option.commitment_value ?? 0));
  const [price, setPrice] = useState(String(option.commitment_price_per_unit ?? 0));
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit “{option.name}”</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <Field label="Commitment value">
            {(p) => <NumericInput {...p} value={value} onChange={(e) => setValue(e.target.value)} />}
          </Field>
          <Field label="Unit price">
            {(p) => <NumericInput {...p} value={price} onChange={(e) => setPrice(e.target.value)} />}
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={pending}>
            Close
          </Button>
          <Button variant="primary" disabled={pending} onClick={() => onSubmit(Number(value) || 0, Number(price) || 0)}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
